var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Typography, Button, Select, Card, Space, Upload, Input, message as antMessage, Image } from 'antd';
import { CloseOutlined, PlusOutlined, PlayCircleOutlined, FileImageOutlined, LoadingOutlined } from '@ant-design/icons';
import Icon from '@ant-design/icons';
import classNames from 'classnames';
import { has } from 'lodash';
import { MySelect } from '../../../../../Base/Form';
import { useStore } from '../../../../../../store/rootStore';
import { ReactComponent as QuickRepliesGreenPlus } from '../../../../../../assets/icons/plus/greenPlus.svg';
import { MyTextArea } from '../../../../../Base/Form';
import { isTextMessage, isMediaMessage, MessageContentsType, QuickReplies, MEDIA_TYPE } from '@whatsper/texterchat-common';
import { STORAGE_AGENT_DIR, getFileUrl } from '../../../../../../store/FilesStore';
import { getFileIconByName } from '../../../../../../helpers/fileIcon';
import { getDirection } from '../../../../../../helpers';
import styles from './AddOrEditReplyField.module.scss';
const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
function getCaptionTargetIndex(media) {
    for (let i = media.length - 1; i >= 0; i--) {
        if (media[i].mediaType !== MEDIA_TYPE.DOC)
            return i;
    }
    return media.length > 0 ? 0 : -1;
}
const AddOrEditReplyField = ({ reply, onChange, onCancel, isLoading = false, channelInfo, enableChannelTargets, defaultTargets, }) => {
    var _a;
    const [replyText, setReplyText] = useState('');
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedType, setSelectedType] = useState(MessageContentsType.text);
    const [mediaFiles, setMediaFiles] = useState([]);
    const [mediaCaption, setMediaCaption] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [title, setTitle] = useState('');
    const [note, setNote] = useState('');
    const [selectedTargets, setSelectedTargets] = useState(defaultTargets || []);
    const { t } = useTranslation();
    const { departmentsStore, loginStore, files, channels } = useStore();
    const encodeTarget = (target) => `${target.name}||${target.accountId}`;
    const handleTargetsChange = (values) => {
        setSelectedTargets(values.map((value) => {
            const [name, accountId] = value.split('||');
            return { name: name, accountId };
        }));
    };
    const resolveTarget = () => {
        if (enableChannelTargets)
            return selectedTargets;
        return (reply === null || reply === void 0 ? void 0 : reply.send.target) || (channelInfo ? [{ name: channelInfo.name, accountId: channelInfo.accountId }] : []);
    };
    const isTextReply = reply && reply.send.contents.length === 1 && isTextMessage(reply.send.contents[0]);
    const isMediaReply = reply && reply.send.contents.length === 1 && isMediaMessage(reply.send.contents[0]);
    const isEditMode = !!reply;
    const captionTargetIndex = getCaptionTargetIndex(mediaFiles);
    const captionType = captionTargetIndex >= 0 ? mediaFiles[captionTargetIndex].mediaType : undefined;
    const resolvedTargets = resolveTarget();
    const targetAdapters = resolvedTargets
        .map((target) => channels.getAccountAdapter({ id: target.accountId, name: target.name }))
        .filter((adapter) => !!adapter);
    const captionLimits = captionType
        ? targetAdapters.map((adapter) => adapter.messageLimits.media[captionType])
        : [];
    const skipCaption = captionTargetIndex === -1 ||
        captionLimits.some((captionLimit) => !captionLimit || (has(captionLimit, 'caption') && !captionLimit.caption));
    const captionMaxLength = captionLimits.length
        ? Math.min(...captionLimits.map((captionLimit) => { var _a; return (_a = captionLimit === null || captionLimit === void 0 ? void 0 : captionLimit.captionMaxLength) !== null && _a !== void 0 ? _a : 1024; }))
        : 1024;
    const QUICK_REPLY_TYPES = [
        {
            value: MessageContentsType.text,
            label: t('textMessage'),
            description: t('textMessageDescription'),
            available: true,
        },
        {
            value: MessageContentsType.media,
            label: t('mediaMessage'),
            description: t('mediaMessageDescription'),
            available: true,
        },
    ];
    useEffect(() => {
        var _a;
        if (reply) {
            if (isTextReply) {
                setSelectedType(MessageContentsType.text);
                const textContent = reply.send.contents[0];
                if (isTextMessage(textContent)) {
                    setReplyText(textContent.text);
                }
            }
            else if (isMediaReply) {
                setSelectedType(MessageContentsType.media);
                const mediaContent = reply.send.contents[0];
                if (isMediaMessage(mediaContent)) {
                    const captionIndex = getCaptionTargetIndex(mediaContent.media);
                    const caption = captionIndex >= 0 ? ((_a = mediaContent.media[captionIndex]) === null || _a === void 0 ? void 0 : _a.caption) || '' : '';
                    setMediaCaption(caption);
                    const existingMediaFiles = mediaContent.media
                        .filter((media) => media.fileId)
                        .map((media) => ({
                        fileId: media.fileId,
                        filename: media.filename,
                        contentType: media.contentType,
                        mediaType: media.mediaType,
                        preview: getFileUrl(media.fileId),
                    }));
                    setMediaFiles(existingMediaFiles);
                }
            }
            else {
                const firstContent = reply.send.contents[0];
                setSelectedType(firstContent.type);
            }
            const departments = reply.display.departments;
            if (departments && Array.isArray(departments)) {
                setSelectedDepartments(departments);
            }
            if (reply.display.title) {
                setTitle(reply.display.title);
            }
            if (reply.display.note) {
                setNote(reply.display.note);
            }
            if (reply.send.target) {
                setSelectedTargets(reply.send.target);
            }
        }
    }, [reply, isTextReply, isMediaReply]);
    const handleChangeReply = (value) => {
        setReplyText(value);
    };
    const handleChangeDepartments = (value) => {
        setSelectedDepartments(value);
    };
    const handleTypeChange = (value) => {
        setSelectedType(value);
        if (value === MessageContentsType.text) {
            setReplyText('');
            setMediaFiles([]);
            setMediaCaption('');
        }
        else if (value === MessageContentsType.media) {
            setReplyText('');
        }
    };
    const getMediaType = (file) => {
        const type = file.type;
        if (type.startsWith('image/'))
            return MEDIA_TYPE.IMAGE;
        if (type.startsWith('video/'))
            return MEDIA_TYPE.VIDEO;
        if (type.startsWith('audio/'))
            return MEDIA_TYPE.AUDIO;
        return MEDIA_TYPE.DOC;
    };
    const handleMediaUpload = (file) => {
        const mediaType = getMediaType(file);
        if (![MEDIA_TYPE.IMAGE, MEDIA_TYPE.VIDEO, MEDIA_TYPE.DOC].includes(mediaType)) {
            antMessage.error(t('onlyImagesVideosDocsSupported'));
            return false;
        }
        const preview = mediaType !== MEDIA_TYPE.DOC ? URL.createObjectURL(file) : undefined;
        setMediaFiles(prev => [
            ...prev,
            {
                file,
                preview,
                mediaType,
                filename: file.name,
                contentType: file.type,
            }
        ]);
        return false;
    };
    const handleRemoveMedia = (index) => {
        setMediaFiles(prev => {
            const newFiles = [...prev];
            if (newFiles[index].preview) {
                URL.revokeObjectURL(newFiles[index].preview);
            }
            newFiles.splice(index, 1);
            return newFiles;
        });
    };
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const baseReply = reply || {
            _id: '',
            send: {
                contents: [],
                target: [],
            },
            display: {
                title: '',
                note: '',
                departments: [],
                position: QuickReplies.DisplayPosition.main,
            },
        };
        let updatedReply;
        if (enableChannelTargets && selectedTargets.length === 0) {
            antMessage.error(t('pleaseSelectQuickReplyChannels'));
            return;
        }
        if (selectedType === MessageContentsType.text) {
            if (!replyText.trim()) {
                antMessage.error(t('pleaseEnterMessageText'));
                return;
            }
            const target = resolveTarget();
            updatedReply = Object.assign(Object.assign({}, baseReply), { send: Object.assign(Object.assign({}, baseReply.send), { contents: [
                        {
                            type: MessageContentsType.text,
                            text: replyText,
                        },
                    ], target }), display: Object.assign(Object.assign({}, baseReply.display), { title: reply ? title.trim() : title.trim() || undefined, note: reply ? note.trim() : note.trim() || undefined, departments: selectedDepartments }) });
        }
        else if (selectedType === MessageContentsType.media) {
            if (mediaFiles.length === 0) {
                antMessage.error(t('pleaseUploadMediaFile'));
                return;
            }
            try {
                setIsUploading(true);
                const filesToUpload = [];
                const fileIndices = [];
                mediaFiles.forEach((mf, index) => {
                    if (mf.file) {
                        filesToUpload.push(mf.file);
                        fileIndices.push(index);
                    }
                });
                let uploadedFiles = [];
                if (filesToUpload.length > 0) {
                    const timestamp = Math.floor(Date.now() / 1000);
                    const userId = (_a = loginStore.userDetails) === null || _a === void 0 ? void 0 : _a._id;
                    const directory = `${STORAGE_AGENT_DIR}/${userId}/${timestamp}`;
                    const uploaded = yield files.uploadFiles(filesToUpload, directory);
                    if (!uploaded || uploaded.length === 0) {
                        throw new Error(t('failedToUploadFiles'));
                    }
                    uploadedFiles = uploaded;
                }
                let uploadedIndex = 0;
                const media = mediaFiles.map((mediaFile, index) => {
                    var _a, _b, _c;
                    let fileId;
                    if (mediaFile.fileId) {
                        fileId = mediaFile.fileId;
                    }
                    else {
                        fileId = ((_a = uploadedFiles[uploadedIndex]) === null || _a === void 0 ? void 0 : _a.fileId) || '';
                        uploadedIndex++;
                    }
                    return {
                        caption: !skipCaption && index === captionTargetIndex ? mediaCaption : undefined,
                        filename: mediaFile.filename || ((_b = mediaFile.file) === null || _b === void 0 ? void 0 : _b.name) || '',
                        contentType: mediaFile.contentType || ((_c = mediaFile.file) === null || _c === void 0 ? void 0 : _c.type) || '',
                        mediaType: mediaFile.mediaType,
                        fileId,
                    };
                });
                const target = resolveTarget();
                updatedReply = Object.assign(Object.assign({}, baseReply), { send: Object.assign(Object.assign({}, baseReply.send), { contents: [
                            {
                                type: MessageContentsType.media,
                                media,
                            },
                        ], target }), display: Object.assign(Object.assign({}, baseReply.display), { title: reply ? title.trim() : title.trim() || undefined, note: reply ? note.trim() : note.trim() || undefined, departments: selectedDepartments }) });
                if (uploadedFiles.length > 0) {
                    antMessage.success(t('successfullyUploadedFiles', { count: uploadedFiles.length }));
                }
            }
            catch (error) {
                console.error('Error uploading media files:', error);
                antMessage.error(t('failedToUploadMediaFiles'));
                setIsUploading(false);
                return;
            }
            finally {
                setIsUploading(false);
            }
        }
        else {
            antMessage.error(t('messageTypeNotSupported'));
            return;
        }
        yield onChange(updatedReply, !reply);
    });
    if (reply && !isTextReply && !isMediaReply) {
        return (React.createElement("div", { className: styles.addOrEditReplyWrapper, dir: getDirection() },
            React.createElement("div", { className: styles.addTitleReplies },
                React.createElement(Icon, { component: QuickRepliesGreenPlus }),
                React.createElement(Text, { className: styles.repliesAddTitleText },
                    React.createElement(Trans, { i18nKey: "editQuickReply" }))),
            React.createElement("div", { className: styles.contentWrapper },
                React.createElement("div", { className: styles.editOnlyMessage },
                    React.createElement(Text, null,
                        React.createElement(Trans, { i18nKey: "editOnlyTextAndMedia" })))),
            React.createElement("div", { className: styles.addReplyBottom },
                React.createElement(Button, { className: classNames('big', styles.addReplyCancelButton), onClick: onCancel },
                    React.createElement(Text, null,
                        React.createElement(Trans, { i18nKey: "cancel" }))))));
    }
    const currentType = QUICK_REPLY_TYPES.find((type) => type.value === selectedType);
    const isSaveDisabled = (selectedType === MessageContentsType.text && !replyText.trim()) ||
        (selectedType === MessageContentsType.media && mediaFiles.length === 0) ||
        (selectedType === MessageContentsType.media && !skipCaption && mediaCaption.length > captionMaxLength) ||
        (!!enableChannelTargets && selectedTargets.length === 0);
    return (React.createElement("div", { className: styles.addOrEditReplyWrapper, dir: getDirection() },
        React.createElement("div", { className: styles.headerSection },
            React.createElement(Space, { align: "center", size: 12 },
                React.createElement(Icon, { component: QuickRepliesGreenPlus, className: styles.headerIcon }),
                React.createElement(Typography.Title, { level: 4, className: styles.headerTitle }, reply ? React.createElement(Trans, { i18nKey: "editQuickReply" }) : React.createElement(Trans, { i18nKey: "createNewQuickReply" })))),
        React.createElement("div", { className: styles.contentWrapper },
            (enableChannelTargets || channelInfo) && (React.createElement(Card, { size: "small", className: styles.stepCard, title: React.createElement(Trans, { i18nKey: "quickReplyTargetChannels" }) },
                React.createElement(MySelect, { value: selectedTargets.map(encodeTarget), onChange: handleTargetsChange, placeholder: t('selectQuickReplyChannels'), mode: "multiple", allowClear: true, disabled: !enableChannelTargets, className: styles.departmentSelect, optionLabelProp: "label" }, channels.accounts.map((account) => {
                    var _a, _b;
                    const value = `${account.channel}||${account.accountId}`;
                    const label = account.title || ((_a = account.metadata) === null || _a === void 0 ? void 0 : _a.accountTitle) || account.accountId;
                    return (React.createElement(Option, { key: value, value: value, label: label },
                        React.createElement(Space, { size: 8, align: "center" },
                            React.createElement("img", { src: (_b = channels.themes[account.accountId]) === null || _b === void 0 ? void 0 : _b.icon, className: styles.channelOptionIcon, alt: "" }),
                            t(`${account.channel}Channel`, { defaultValue: account.channel }),
                            " (",
                            label,
                            ")")));
                })))),
            React.createElement(Card, { size: "small", className: styles.stepCard, title: React.createElement(Trans, { i18nKey: "selectMessageType" }) },
                React.createElement(Select, { direction: getDirection(), value: selectedType, onChange: handleTypeChange, disabled: isEditMode, className: styles.typeSelect, size: "large", placeholder: t('chooseMessageType'), optionLabelProp: "label" }, QUICK_REPLY_TYPES.map((type) => (React.createElement(Option, { key: type.value, value: type.value, label: type.label, disabled: !type.available },
                    React.createElement("div", { className: styles.typeOptionWrapper },
                        React.createElement(Space, { direction: "vertical", size: 2 },
                            React.createElement(Text, { strong: true }, type.label),
                            React.createElement(Text, { type: "secondary", className: styles.typeOptionDescription }, type.description))))))),
                isEditMode && (React.createElement(Text, { type: "secondary", className: styles.editModeNote },
                    React.createElement(Trans, { i18nKey: "cannotChangeTypeInEditMode" })))),
            React.createElement(Card, { size: "small", className: styles.stepCard, title: React.createElement(Trans, { i18nKey: "configureMessage" }) }, selectedType === MessageContentsType.text ? (React.createElement(Space, { direction: "vertical", size: 16, className: styles.textConfigSection },
                React.createElement("div", null,
                    React.createElement(Text, { strong: true, className: styles.fieldLabel },
                        React.createElement(Trans, { i18nKey: "messageContent" })),
                    React.createElement(MyTextArea, { placeholderText: t('messageText'), text: replyText, setText: handleChangeReply, containerClassName: styles.repliesTextEditor, emojiPicker: true })))) : selectedType === MessageContentsType.media ? (React.createElement(Space, { direction: "vertical", size: 16, className: styles.mediaUploadSection },
                React.createElement("div", null,
                    React.createElement(Text, { strong: true, className: styles.fieldLabel },
                        React.createElement(Trans, { i18nKey: "uploadMediaFiles" })),
                    React.createElement(Upload, { listType: "picture-card", beforeUpload: handleMediaUpload, showUploadList: false, accept: "image/*,video/*,.pdf,.doc,.docx", multiple: true },
                        React.createElement("div", null,
                            React.createElement(PlusOutlined, null),
                            React.createElement("div", { className: styles.uploadText },
                                React.createElement(Trans, { i18nKey: "upload" }))))),
                mediaFiles.length > 0 && (React.createElement("div", { className: styles.mediaPreviewGrid }, mediaFiles.map((mediaFile, index) => {
                    var _a, _b, _c, _d;
                    return (React.createElement("div", { key: (_c = (_a = mediaFile.fileId) !== null && _a !== void 0 ? _a : (_b = mediaFile.file) === null || _b === void 0 ? void 0 : _b.uid) !== null && _c !== void 0 ? _c : index, className: styles.mediaPreviewItem },
                        React.createElement("div", { className: styles.mediaPreviewContent }, mediaFile.mediaType === MEDIA_TYPE.IMAGE ? (React.createElement(Image, { src: mediaFile.preview, alt: mediaFile.filename || ((_d = mediaFile.file) === null || _d === void 0 ? void 0 : _d.name) || 'Image', className: styles.mediaPreviewImage, wrapperClassName: styles.mediaPreviewImage, preview: false })) : mediaFile.mediaType === MEDIA_TYPE.VIDEO ? (React.createElement("div", { className: styles.mediaPreviewVideo },
                            React.createElement("video", { src: mediaFile.preview }),
                            React.createElement(PlayCircleOutlined, { className: styles.mediaPreviewVideoIcon }))) : mediaFile.mediaType === MEDIA_TYPE.DOC ? ((() => {
                            var _a, _b, _c;
                            const FileIcon = getFileIconByName(mediaFile.filename || ((_a = mediaFile.file) === null || _a === void 0 ? void 0 : _a.name), mediaFile.contentType || ((_b = mediaFile.file) === null || _b === void 0 ? void 0 : _b.type));
                            return (React.createElement("div", { className: styles.mediaPreviewPlaceholder },
                                React.createElement(FileIcon, { width: 64, height: 80 }),
                                React.createElement(Text, { className: styles.docFilename, ellipsis: true }, mediaFile.filename || ((_c = mediaFile.file) === null || _c === void 0 ? void 0 : _c.name))));
                        })()) : (React.createElement("div", { className: styles.mediaPreviewPlaceholder },
                            React.createElement(FileImageOutlined, null)))),
                        React.createElement(Button, { type: "primary", shape: "circle", size: "small", icon: React.createElement(CloseOutlined, null), className: styles.mediaPreviewDeleteBtn, onClick: () => handleRemoveMedia(index), "aria-label": t('removeMediaFile'), title: t('removeMediaFile') })));
                }))),
                !skipCaption && (React.createElement("div", null,
                    React.createElement(Text, { strong: true, className: styles.fieldLabel },
                        React.createElement(Trans, { i18nKey: "captionOptional" })),
                    React.createElement(TextArea, { dir: getDirection(), value: mediaCaption, onChange: (e) => setMediaCaption(e.target.value), placeholder: t('captionPlaceholder'), rows: 3, maxLength: captionMaxLength, showCount: true }))))) : (React.createElement("div", { className: styles.comingSoonMessage },
                React.createElement(Typography.Title, { level: 3, className: styles.comingSoonTitle },
                    React.createElement(Trans, { i18nKey: "messagesComingSoon", values: { type: currentType === null || currentType === void 0 ? void 0 : currentType.label } })),
                React.createElement(Text, { className: styles.comingSoonDescription }, currentType === null || currentType === void 0 ? void 0 : currentType.description),
                React.createElement("div", { className: styles.comingSoonSubtext },
                    React.createElement(Text, null,
                        React.createElement(Trans, { i18nKey: "featureComingSoon" })))))),
            React.createElement(Card, { size: "small", className: styles.stepCard, title: React.createElement(Space, { size: 8, align: "center", wrap: true },
                    React.createElement(Trans, { i18nKey: "targetDepartments" }),
                    React.createElement(Text, { type: "secondary" },
                        React.createElement(Trans, { i18nKey: "targetDepartmentsHelperMessage" }))) },
                React.createElement(MySelect, { value: selectedDepartments, onChange: handleChangeDepartments, placeholder: t('selectDepartmentsOptional'), mode: "multiple", allowClear: true, className: styles.departmentSelect }, (_a = departmentsStore.departments) === null || _a === void 0 ? void 0 : _a.map((department) => (React.createElement(Option, { key: department._id, value: department._id },
                    React.createElement(Space, { className: styles.departmentOption },
                        React.createElement("div", { className: styles.departmentColorDot, style: { backgroundColor: department.color || '#d9d9d9' } }),
                        department.name)))))),
            React.createElement(Card, { size: "small", className: classNames(styles.stepCard, styles.configCard), title: React.createElement(Trans, { i18nKey: "quickReplyInfo" }) },
                React.createElement(Space, { direction: "vertical", size: 16, className: styles.mediaUploadSection },
                    React.createElement("div", null,
                        React.createElement(Text, { strong: true, className: styles.fieldLabel },
                            React.createElement(Trans, { i18nKey: "quickReplyTitle" }),
                            " ",
                            React.createElement(Text, { type: "secondary" },
                                React.createElement(Trans, { i18nKey: "quickReplyTitleOptional" }))),
                        React.createElement(Input, { dir: getDirection(), value: title, onChange: (e) => setTitle(e.target.value), placeholder: t('quickReplyTitlePlaceholder'), maxLength: 100, showCount: true, size: "large" }),
                        React.createElement(Text, { type: "secondary", className: styles.fieldHint },
                            React.createElement(Trans, { i18nKey: "quickReplyTitleHint" }))),
                    React.createElement("div", null,
                        React.createElement(Text, { strong: true, className: styles.fieldLabel },
                            React.createElement(Trans, { i18nKey: "quickReplyNote" }),
                            " ",
                            React.createElement(Text, { type: "secondary" },
                                React.createElement(Trans, { i18nKey: "quickReplyTitleOptional" }))),
                        React.createElement(TextArea, { dir: getDirection(), value: note, onChange: (e) => setNote(e.target.value), placeholder: t('quickReplyNotePlaceholder'), rows: 2, maxLength: 200, showCount: true }),
                        React.createElement(Text, { type: "secondary", className: styles.fieldHint },
                            React.createElement(Trans, { i18nKey: "quickReplyNoteHint" })))))),
        React.createElement("div", { className: styles.footerActions },
            React.createElement(Button, { size: "large", onClick: onCancel, disabled: isUploading || isLoading },
                React.createElement(Trans, { i18nKey: "cancel" })),
            React.createElement(Button, { type: "primary", size: "large", onClick: handleSave, disabled: isSaveDisabled || isUploading || isLoading, loading: isUploading || isLoading, icon: isUploading || isLoading ? React.createElement(LoadingOutlined, null) : undefined }, isUploading ? React.createElement(Trans, { i18nKey: "uploading" }) : React.createElement(Trans, { i18nKey: "saveChanges" })))));
};
export default AddOrEditReplyField;
